import { createFileRoute } from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { useEffect } from "react";

export const Route = createFileRoute("/faq")({
  component: FAQ,
});

function FAQ() {
  useEffect(() => {
    document.title = "FAQ — 24Boda";
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/40 bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <a href="/" className="flex items-center">
            <img src="/logo.png" alt="24Boda" className="h-10 w-auto" />
          </a>
          <nav className="hidden items-center gap-8 text-sm font-medium text-muted-foreground md:flex">
            <a href="/" className="transition-colors hover:text-foreground">
              Home
            </a>
            <a href="/about" className="transition-colors hover:text-foreground">
              About
            </a>
            <a href="/blog" className="transition-colors hover:text-foreground">
              Blog
            </a>
            <a href="/faq" className="text-foreground">
              FAQ
            </a>
            <a href="/join-riders" className="transition-colors hover:text-foreground">
              Riders
            </a>
          </nav>
          <a
            href="/#download"
            className="inline-flex h-10 items-center justify-center rounded-lg bg-primary px-6 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Download App
          </a>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="border-b border-border bg-secondary/30 py-16 sm:py-24">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
            <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
              Frequently Asked Questions
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
              Everything you need to know about using 24Boda for delivery.
            </p>
          </div>
        </section>

        {/* FAQ Sections */}
        <section className="py-16 sm:py-24">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            {/* For Customers */}
            <div className="mb-12">
              <h2 className="mb-8 text-2xl font-bold text-foreground">For Customers</h2>
              <div className="space-y-4">
                <FAQItem
                  question="How do I book a delivery?"
                  answer="Download the 24Boda app from Google Play or App Store, create an account, enter your pickup and drop-off locations, choose package size, and confirm. A nearby rider will accept within minutes."
                />
                <FAQItem
                  question="How much does delivery cost?"
                  answer="Prices are fixed based on distance and package size. You'll see the exact fare before confirming. No hidden fees. Typical deliveries range from UGX 3,000 to UGX 15,000 depending on distance."
                />
                <FAQItem
                  question="Can I track my delivery?"
                  answer="Yes! You can track your delivery in real-time from pickup to drop-off. You'll see the rider's location, name, plate number, and estimated arrival time."
                />
                <FAQItem
                  question="What if my package is damaged or lost?"
                  answer="All deliveries are insured. If anything goes wrong, contact our support team immediately through the app. We'll investigate and provide compensation if necessary."
                />
                <FAQItem
                  question="What payment methods do you accept?"
                  answer="We accept Mobile Money (MTN, Airtel), credit/debit cards, and cash on delivery. Payment is processed securely through the app."
                />
                <FAQItem
                  question="What size packages can I send?"
                  answer="We handle packages up to 15kg that fit in a standard boda delivery box (50cm x 50cm x 40cm). For larger items, contact us for custom arrangements."
                />
              </div>
            </div>

            {/* For Riders */}
            <div className="mb-12">
              <h2 className="mb-8 text-2xl font-bold text-foreground">For Riders</h2>
              <div className="space-y-4">
                <FAQItem
                  question="How much can I earn as a rider?"
                  answer="Earnings vary based on trips completed. Part-time riders (10-15 trips/day) earn UGX 20,000-30,000. Full-time riders (25-35 trips/day) earn UGX 50,000-70,000. You keep 80% of every fare."
                />
                <FAQItem
                  question="What do I need to become a rider?"
                  answer="You need: valid national ID, driver's license, registered vehicle with plate number, smartphone with internet, and must be 18+ years old. We'll also conduct a background check."
                />
                <FAQItem
                  question="How do I get paid?"
                  answer="You get paid daily! Earnings are available for withdrawal to your mobile money account every evening. No waiting, no delays."
                />
                <FAQItem
                  question="Do I need my own motorcycle?"
                  answer="Yes, you need your own vehicle (boda, bicycle, or car). We don't provide vehicles, but you can use what you already own."
                />
                <FAQItem
                  question="What happens if I have an accident?"
                  answer="All riders are covered by insurance while on delivery. We also provide 24/7 support for emergencies. Your safety is our priority."
                />
                <FAQItem
                  question="Can I choose my working hours?"
                  answer="Yes! You work whenever you want. Accept trips when you're available, decline when you're not. Total flexibility."
                />
              </div>
            </div>

            {/* General */}
            <div>
              <h2 className="mb-8 text-2xl font-bold text-foreground">General</h2>
              <div className="space-y-4">
                <FAQItem
                  question="Where does 24Boda operate?"
                  answer="We currently operate across Kampala and surrounding areas. We're expanding to more cities in Uganda soon."
                />
                <FAQItem
                  question="Is 24Boda available 24/7?"
                  answer="Yes! We have riders available around the clock. Whether it's early morning or late night, you can book a delivery anytime."
                />
                <FAQItem
                  question="How do I contact customer support?"
                  answer="Contact us via: Phone (+256 700 000 000), Email (hello@24boda.com), or through the in-app support chat. We're available 24/7."
                />
                <FAQItem
                  question="Are your riders verified?"
                  answer="Yes! Every rider goes through ID verification, background checks, and training before joining our platform. Safety is our top priority."
                />
              </div>
            </div>
          </div>
        </section>

        {/* Still have questions CTA */}
        <section className="border-t border-border bg-secondary/30 py-16">
          <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-foreground">Still have questions?</h2>
            <p className="mt-4 text-muted-foreground">
              Our support team is available 24/7 to help you.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <a
                href="tel:+256700000000"
                className="inline-flex h-12 items-center justify-center rounded-lg bg-primary px-8 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Call Us
              </a>
              <a
                href="mailto:hello@24boda.com"
                className="inline-flex h-12 items-center justify-center rounded-lg border border-border bg-background px-8 text-sm font-semibold text-foreground transition-colors hover:bg-secondary"
              >
                Email Us
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-secondary/20 py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="flex items-center">
              <img src="/logo.png" alt="24Boda" className="h-8 w-auto" />
            </div>
            <div className="text-center text-sm text-muted-foreground">
              © {new Date().getFullYear()} 24Boda. All rights reserved. Kampala, Uganda.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="rounded-lg border border-border bg-card">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between p-6 text-left transition-colors hover:bg-secondary/50"
      >
        <span className="font-semibold text-foreground">{question}</span>
        <ChevronDown
          className={`size-5 shrink-0 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      {isOpen && (
        <div className="border-t border-border px-6 pb-6 pt-4">
          <p className="text-sm text-muted-foreground">{answer}</p>
        </div>
      )}
    </div>
  );
}
